// CssBaseline needs to be imported before all other styles to avoid errors
import { CssBaseline, Stack, ThemeProvider } from "@mui-wrapper";
import { Outlet } from "react-router-dom";
import { IReactChildrenOnly } from "@custom-types/types";
import { runtimeConfig } from "@src/runtime-config";
import { useUlmConfig } from "@hooks/use-ulm-config";
import { mainTheme } from "@styles/theme";
import { ToastProvider } from "@flat-ui";
import { useTrackPageLoad } from "@hooks/use-track-page-load";
import { ErrorHandlerProvider } from "@context-providers/error-handler/error-handler-provider";
import { ErrorBoundary } from "@context-providers/error-boundary/error-boundary";
import { useEffect } from "react";
import { useLegalRedirect } from "@hooks/use-legal-redirect";
import { isSystemThemeEnabled } from "@utils/env-utils";

// TODO: Remove this gate in https://faro01.atlassian.net/browse/ST-4922
// Until dark mode is released, the page follows the system colour scheme only on localhost and the
// numbered dev and staging environments. Everywhere else it stays pinned to light, as before.
const themeMode = isSystemThemeEnabled() ? "system" : "light";

/**
 * All the context providers should be added here to make them available across the whole application.
 *
 * @returns all the react context providers and it's initialization.
 */
function AllContextProviders({ children }: IReactChildrenOnly): JSX.Element {
  return (
    // The colour scheme follows the system where `themeMode` is "system", and is light otherwise.
    //
    // `storageManager={null}` keeps MUI from reading a mode stored under `mui-mode`, a key shared by
    // every MUI app on the origin, and from storing one itself.
    //
    // `forceThemeRerender` recalculates the theme when the scheme changes, so `theme.palette` reads
    // follow the active scheme.
    <ThemeProvider
      theme={mainTheme}
      defaultMode={themeMode}
      storageManager={null}
      forceThemeRerender
    >
      <CssBaseline />
      <ToastProvider>
        <ErrorHandlerProvider>
          <ErrorBoundary>{children}</ErrorBoundary>
        </ErrorHandlerProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

// Keep this layout hook-free.
// Legal routes bypass LocalizeProvider in main.tsx and still need to render safely.
function AppLayout(): JSX.Element {
  return (
    <AllContextProviders>
      <Stack sx={{ height: "100vh" }} data-testid="homepage">
        <Outlet />
      </Stack>
    </AllContextProviders>
  );
}

// These hooks depend (directly/indirectly) on Localize context.
// They must run only when LocalizeProvider is present (non-legal routes).
function AppWithInitialization(): JSX.Element {
  // Initialize ULM configuration
  useUlmConfig();

  // Initializes the tracking of the user in the entry page API
  useTrackPageLoad();

  return <AppLayout />;
}

/**
 * Entry point deciding on the structure of the app
 */
export function App(): JSX.Element {
  const { isLegalRoute } = useLegalRedirect();

  useEffect(() => {
    // Log app version at start since we don't show it anywhere
    // eslint-disable-next-line no-console -- It is ok to show the version in the console
    console.log(`App version: ${runtimeConfig.appVersion}`);

    // eslint-disable-next-line no-console, @typescript-eslint/no-magic-numbers -- It is ok to show the commit hash in the console
    console.log(`Commit Hash: ${runtimeConfig.commitId?.slice(0, 8)}`);
  }, []);

  // On legal routes we intentionally remove LocalizeProvider,
  // so render the hook-free layout to avoid Localize context errors.
  if (isLegalRoute) {
    return <AppLayout />;
  }

  return <AppWithInitialization />;
}
